import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Divider,
  Tab,
  Tabs,
  Link,
} from "@mui/material";
import { Google as GoogleIcon } from "@mui/icons-material";
import { useSupabase } from "../providers/SupabaseProvider";
import Page from "../components/Page";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const {
    session,
    signInWithEmail,
    signInWithGoogle,
    signUp,
    resetPassword,
    loading,
    signInAnonymously,
  } = useSupabase();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [playerName, setPlayerName] = useState<string>("");
  const [playerAge, setPlayerAge] = useState<string>("");
  const [message, setMessage] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      navigate("/budget");
    }
  }, [session, navigate]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setMessage("");
    setError(null);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    await signInWithEmail(email, password);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }

    await signUp(email, password);
    setMessage("Check your email to confirm your account!");
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    await signInWithEmail(email);
    setMessage("Check your email for the magic link!");
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    if (!email) {
      setError("Email is required");
    } else {
      await resetPassword(email);
      setMessage("Check your email for password reset instructions!");
    }
  };

  return (
    <Page title="Welcome">
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: "auto" }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Welcome to Ristocat
          </Typography>

          <TextField
            fullWidth
            label="Your name"
            placeholder="e.g. Alex"
            variant="outlined"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Your age"
            placeholder="e.g. 12"
            variant="outlined"
            value={playerAge}
            onChange={(e) =>
              setPlayerAge(e.target.value.replace(/[^0-9]/g, ""))
            }
            sx={{ mb: 2 }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1 }}
          />
          {/* Quick-start option */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2, mb: 3 }}
            disabled={loading || playerName.trim().length === 0}
            onClick={async () => {
              // Save the name so provider can send to backend once session exists
              const ageValue =
                playerAge.trim().length === 0 ? "0" : playerAge.trim();
              const username = `${playerName.trim()} - ${ageValue}`;
              localStorage.setItem("pendingUsername", username);
              console.log(`Set pendingUsername to: \"${username}\"`);
              await signInAnonymously();
              // After anonymous sign-in, navigate to the main game
              navigate("/budget");
            }}
          >
            {loading ? "Preparing..." : "Dive In"}
          </Button>

          <div
            style={{ display: "none" }} // TODO: re-enable this
          >
            <Divider sx={{ my: 2 }}>OR Sign In</Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={() => signInWithGoogle()}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Continue with Google
            </Button>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Sign In" />
              <Tab label="Sign Up" />
              <Tab label="Magic Link" />
            </Tabs>

            {(message || error) && (
              <Alert
                severity={
                  message.includes("Check your email")
                    ? "success"
                    : error
                      ? "error"
                      : "info"
                }
                sx={{ mt: 2 }}
              >
                {message || error}
              </Alert>
            )}

            <TabPanel value={tabValue} index={0}>
              <Box component="form" onSubmit={handleSignIn}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <Box sx={{ textAlign: "center" }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handlePasswordReset}
                    sx={{ textDecoration: "none" }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box component="form" onSubmit={handleSignUp}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm Password"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? "Signing up..." : "Sign Up"}
                </Button>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box component="form" onSubmit={handleMagicLink}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Magic Link"}
                </Button>
              </Box>
            </TabPanel>
          </div>
        </Paper>
      </Box>
    </Page>
  );
};

export default Home;
